import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import { Userprofile } from "../../Entities/user-profile.entity";
import { openNotification } from "../Notification/Index";
import { getFileMimeTypeImage } from "../../Utils/generic-functions";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAuth } from "../../Context/AuthContext/AuthContext";
dayjs.extend(relativeTime);

const { Dragger } = Upload;

interface ResumeSelectorProps {
  handleResumeFileUpload: (file: File) => void;
  handleExistingResumeSelection?: (userProfile: Userprofile) => void;
  resumeFile: File | null;
  selectedProfile: Userprofile | null;
  handleRemoveResumeFile: () => void;
  handleContinue: () => Promise<void>;
}

const ResumeSelector: React.FC<ResumeSelectorProps> = ({
  handleResumeFileUpload,
  handleExistingResumeSelection,
  resumeFile,
  selectedProfile,
  handleRemoveResumeFile,
  handleContinue,
}) => {
  const [userProfiles, setUserProfiles] = useState<Userprofile[] | []>([]);

  const { isUserLoggedIn } = useAuth();

  const profileMatcherService = new ProfileMatcherService();

  const fetchAllUserProfiles = async () => {
    try {
      const data = await profileMatcherService.getAllUploadedProfiles();
      setUserProfiles(
        data.sort(
          (a: Userprofile, b: Userprofile) =>
            new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime(),
        ),
      );
    } catch (error) {
      console.error("Error while fetching all user profiles", error);
      setUserProfiles([]);
    }
  };

  useEffect(() => {
    if (isUserLoggedIn) {
      fetchAllUserProfiles();
    }
  }, [isUserLoggedIn]);

  const handleContinueButtonClick = async () => {
    if (!resumeFile && !selectedProfile) {
      openNotification(
        "error",
        "No resume file or selected profile",
        "Please upload a resume file or select a previously uploaded resume.",
      );
      return;
    }
    await handleContinue();
  };

  const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  ];

  const beforeUpload = (file: File): boolean => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const isValidType = ALLOWED_MIME_TYPES.includes(file.type);

    if (!isValidType) {
      openNotification(
        "error",
        "Invalid File Type",
        "Please upload a PDF, DOC or DOCX file only.",
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      openNotification(
        "error",
        "File Too Large",
        "File size must be less than 5MB.",
      );
      return false;
    }

    return true;
  };

  const uploadResumeFile = async (options: any) => {
    const { file, onSuccess, onError } = options;

    try {
      handleResumeFileUpload(file as File);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    disabled: resumeFile || selectedProfile ? true : false,
    maxCount: 1,
    accept: ".pdf,.doc,.docx",
    customRequest: uploadResumeFile,
    beforeUpload,
    onChange(info) {
      if (info.fileList.length > 1) {
        openNotification(
          "error",
          "Multiple Files Detected",
          "Please upload only one file at a time.",
        );
        info.fileList = [info.fileList[0]];
        return;
      }

      const { status } = info.file;
      if (status === "done") {
        openNotification("success", "File Selected", "File ready for upload.");
      } else if (status === "error") {
      }
    },
    onDrop(e) {
      if (e.dataTransfer.files.length > 1) {
        openNotification(
          "error",
          "Multiple Files Detected",
          "Please drop only one file at a time.",
        );
      }
    },
  };

  return (
    <div className="resume-selector-container">
      <div>
        {resumeFile ? (
          <div className="upload-preview">
            <div className="upload-preview-item">
              <div className="upload-preview-image-wrapper">
                <img
                  src={getFileMimeTypeImage(
                    resumeFile?.name || selectedProfile?.filename || "pdf",
                  )}
                  alt={
                    resumeFile?.name || selectedProfile?.filename || "Resume"
                  }
                  className="upload-preview-image"
                />
                <CloseOutlined
                  onClick={() => {
                    if (resumeFile) {
                      handleRemoveResumeFile();
                    }
                  }}
                  className="upload-preview-cancel"
                />
              </div>
              <span className="upload-preview-name">
                {resumeFile?.name || selectedProfile?.filename || "Resume"}
              </span>
              {resumeFile && (
                <span className="upload-preview-size">
                  {resumeFile.size
                    ? `${(resumeFile.size / 1024).toFixed(2)} KB`
                    : "Unknown size"}
                </span>
              )}
              {selectedProfile && (
                <span className="upload-preview-size">Previously uploaded</span>
              )}
            </div>
          </div>
        ) : (
          <Dragger {...props}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Upload PDF, DOC or DOCX files. Maximum file size allowed is 5 MB.
            </p>
          </Dragger>
        )}
      </div>

      {handleExistingResumeSelection &&
        userProfiles &&
        userProfiles.length > 0 && (
          <Divider orientation="center" style={{ margin: "0px" }}>
            <span>or</span>
          </Divider>
        )}

      {handleExistingResumeSelection &&
        userProfiles &&
        userProfiles.length > 0 && (
          <div className="resume-selector-file-list-container">
            <Typography.Text strong style={{ margin: 0 }}>
              Continue with previously uploaded resume
            </Typography.Text>

            <div
              className={`resume-selector-file-list-items-container ${
                resumeFile ? "disabled-container" : ""
              }`}
            >
              {userProfiles.map((profile, index) => (
                <div
                  className={`resume-selector-file-list ${
                    selectedProfile?._id === profile._id
                      ? "resume-selector-file-list-selected"
                      : ""
                  }`}
                  key={index}
                  onClick={() => handleExistingResumeSelection(profile)}
                >
                  <Checkbox
                    disabled={resumeFile ? true : false}
                    checked={selectedProfile?._id === profile._id}
                  />
                  <div className="resume-selector-file-list-item-content">
                    <Typography.Text disabled={resumeFile ? true : false}>
                      {profile.filename}
                    </Typography.Text>
                    <small>
                      Last used {dayjs(profile.lastUsedAt).fromNow()}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {(selectedProfile || resumeFile) && (
        <div className="resume-selector-continue-button-container">
          <Button
            type="primary"
            shape="round"
            block
            onClick={handleContinueButtonClick}
            disabled={!resumeFile && !selectedProfile}
            size="middle"
          >
            {resumeFile ? "Continue" : "Analyze Resume"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ResumeSelector;
